/**
 * Collect a diagnostics report blob for the "Send Diagnostics" feature (#1847).
 *
 * Pure data gathering - no side effects, no UI. Returns a plain object
 * suitable for JSON serialization and POSTing to the backend or saving
 * as a file. The blob is whitelisted: only fields explicitly added here
 * end up in the report. Auth tokens, photo content, and survey response
 * text are intentionally excluded.
 */

import { getRecentErrors } from './errorBuffer';
import { getActionLog } from './actionLog';

declare const __GIT_COMMIT__: string;
declare const __GIT_BRANCH__: string;
declare const __BUILD_DATE__: string;
declare const __APP_VERSION__: string;

interface NetworkConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string; // 'wifi' | 'cellular' | 'ethernet' | ... when exposed
}

export interface BandwidthTestResult {
  bytes: number;
  elapsedMs: number;
  mbps: number;
  /** Where the test fetched from (so we can correlate with backend telemetry). */
  endpoint: string;
  /** Set when the test couldn't run (offline, 5xx, etc.) — `mbps` will be 0. */
  error?: string;
}

export interface ApiPingResult {
  url: string;
  ok: boolean;
  status: number;
  elapsedMs: number;
}

export interface BatteryInfo {
  level: number; // 0..1
  charging: boolean;
  chargingTime?: number;
  dischargingTime?: number;
}

/**
 * Per-submission context block. Each sub-page populates this differently
 * (see #1873) — Send Device Report uses 'quick', GPS page uses 'gps' with
 * its sample arrays, Scanner page uses 'scanner' with base64 frames +
 * decode traces. The discriminator field is `type`.
 *
 * #1965 — every variant may carry an optional `attachments` block (notes +
 * compressed iPhone screenshots) collected by `DiagnosticAttachments`.
 */
export interface DiagnosticsAttachmentsBlock {
  notes: string;
  screenshots: Array<{
    filename: string;
    mimeType: 'image/jpeg';
    sizeBytes: number;
    base64: string;
  }>;
}

export type DiagnosticsContext =
  | { type: 'quick'; attachments?: DiagnosticsAttachmentsBlock }
  | { type: 'gps'; gps: unknown; attachments?: DiagnosticsAttachmentsBlock }
  | { type: 'scanner'; scanner: unknown; attachments?: DiagnosticsAttachmentsBlock };

interface SwInfo {
  registered: boolean;
  scope?: string;
  activeScriptUrl?: string;
  waitingScriptUrl?: string;
  updateViaCache?: string;
}

async function getServiceWorkerInfo(): Promise<SwInfo> {
  if (!('serviceWorker' in navigator)) {
    return { registered: false };
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return { registered: false };
    return {
      registered: true,
      scope: reg.scope,
      activeScriptUrl: reg.active?.scriptURL,
      waitingScriptUrl: reg.waiting?.scriptURL,
      updateViaCache: reg.updateViaCache,
    };
  } catch {
    return { registered: false };
  }
}

async function getStorageEstimate(): Promise<{ quota: number; usage: number; pct: number } | null> {
  if (!('storage' in navigator) || !navigator.storage.estimate) return null;
  try {
    const est = await navigator.storage.estimate();
    const quota = est.quota ?? 0;
    const usage = est.usage ?? 0;
    return {
      quota,
      usage,
      pct: quota > 0 ? Math.round((usage / quota) * 10000) / 100 : 0,
    };
  } catch {
    return null;
  }
}

async function getIndexedDbSummary(): Promise<Array<{ db: string; store: string; count: number }>> {
  const result: Array<{ db: string; store: string; count: number }> = [];
  if (!('indexedDB' in window) || !indexedDB.databases) return result;

  try {
    const dbs = await indexedDB.databases();
    for (const dbInfo of dbs) {
      if (!dbInfo.name) continue;
      try {
        const db = await new Promise<IDBDatabase>((resolve, reject) => {
          const req = indexedDB.open(dbInfo.name!);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
          req.onblocked = () => reject(new Error('blocked'));
        });
        for (const storeName of Array.from(db.objectStoreNames)) {
          try {
            const count = await new Promise<number>((resolve, reject) => {
              const tx = db.transaction(storeName, 'readonly');
              const store = tx.objectStore(storeName);
              const req = store.count();
              req.onsuccess = () => resolve(req.result);
              req.onerror = () => reject(req.error);
            });
            result.push({ db: dbInfo.name, store: storeName, count });
          } catch {
            result.push({ db: dbInfo.name, store: storeName, count: -1 });
          }
        }
        db.close();
      } catch {
        // skip dbs we can't open
      }
    }
  } catch {
    // Older browsers (Firefox before recently) don't have indexedDB.databases()
  }
  return result;
}

export interface OfflineStoreDump {
  db: string;
  store: string;
  /** Total rows in the store (rows[] may be capped below this). */
  total: number;
  /** True if rows were capped at MAX_DUMP_ROWS_PER_STORE. */
  truncated: boolean;
  /** Row data with Blob/File values replaced by {__blob,name,type,size} (#2207). */
  rows: unknown[];
}

const MAX_DUMP_ROWS_PER_STORE = 250;

/**
 * Replace Blob/File values with lightweight metadata so the offline dump
 * stays small (KBs) and JSON-serializable — the photo blobs in
 * visit_file_queue are MBs and are uploaded separately, so we never embed
 * them in a diagnostics report (#2207).
 */
export function elideBlobs(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value instanceof Blob) {
    const name = value instanceof File ? value.name : undefined;
    return { __blob: true, name, type: value.type || undefined, size: value.size };
  }
  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
    return { __binary: true, bytes: (value as ArrayBufferView).byteLength ?? (value as ArrayBuffer).byteLength };
  }
  if (Array.isArray(value)) return value.map((v) => elideBlobs(v, seen));
  if (value && typeof value === 'object') {
    if (seen.has(value as object)) return '[circular]';
    seen.add(value as object);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = elideBlobs(v, seen);
    return out;
  }
  return value;
}

/** Dump every offline IndexedDB store's rows (blobs elided, capped per store). */
async function getIndexedDbDump(): Promise<OfflineStoreDump[]> {
  const result: OfflineStoreDump[] = [];
  if (!('indexedDB' in window) || !indexedDB.databases) return result;
  try {
    const dbs = await indexedDB.databases();
    for (const dbInfo of dbs) {
      if (!dbInfo.name) continue;
      try {
        const db = await new Promise<IDBDatabase>((resolve, reject) => {
          const req = indexedDB.open(dbInfo.name!);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
          req.onblocked = () => reject(new Error('blocked'));
        });
        for (const storeName of Array.from(db.objectStoreNames)) {
          try {
            const rows = await new Promise<unknown[]>((resolve, reject) => {
              const tx = db.transaction(storeName, 'readonly');
              const req = tx.objectStore(storeName).getAll(undefined, MAX_DUMP_ROWS_PER_STORE + 1);
              req.onsuccess = () => resolve(req.result as unknown[]);
              req.onerror = () => reject(req.error);
            });
            const truncated = rows.length > MAX_DUMP_ROWS_PER_STORE;
            const kept = (truncated ? rows.slice(0, MAX_DUMP_ROWS_PER_STORE) : rows).map((r) => elideBlobs(r));
            result.push({ db: dbInfo.name, store: storeName, total: rows.length, truncated, rows: kept });
          } catch {
            result.push({ db: dbInfo.name, store: storeName, total: -1, truncated: false, rows: [] });
          }
        }
        db.close();
      } catch {
        // skip dbs we can't open
      }
    }
  } catch {
    // indexedDB.databases() unavailable
  }
  return result;
}

function getLocalStorageSummary(): Array<{ key: string; bytes: number }> {
  const out: Array<{ key: string; bytes: number }> = [];
  // Sensitive keys we don't even include size for - hides their presence too.
  const skip = new Set(['accesstoken', 'refreshtoken', 'password', 'token']);
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (skip.has(key.toLowerCase())) continue;
      const value = localStorage.getItem(key) ?? '';
      out.push({ key, bytes: value.length });
    }
  } catch {
    // localStorage can throw in private mode
  }
  return out;
}

interface GeoFix {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: string;
  ageMs: number;
}

interface GeoState {
  permission: string;
  /** Cached fix if the browser had one within the past 30s. */
  lastFix: GeoFix | null;
  /** Fresh one-shot at submit time, with up to 8s timeout. Null if denied / timed out. */
  freshFix: GeoFix | null;
  /** Reason freshFix is null, if available. */
  freshFixError?: string;
  /** How long the fresh fix attempt took (ms), even when it failed. */
  freshFixElapsedMs?: number;
}

async function getGeolocationState(): Promise<GeoState> {
  let permission = 'unknown';
  try {
    if ('permissions' in navigator) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (navigator.permissions as any).query({ name: 'geolocation' });
      permission = result.state;
    }
  } catch {
    permission = 'query-failed';
  }

  // Two-step fix capture (#1873). First try with `maximumAge: 30000` —
  // returns instantly with a cached fix if the browser has one. Then run
  // a fresh one-shot with no caching to get the most accurate current
  // location. Both go into the payload so support can see both "what the
  // device knew at start" and "what it knows right now."
  const fetchFix = (maxAgeMs: number, timeoutMs: number): Promise<GeoFix | { error: string; elapsedMs: number }> =>
    new Promise((resolve) => {
      if (!('geolocation' in navigator) || permission === 'denied') {
        resolve({ error: 'permission_denied_or_unavailable', elapsedMs: 0 });
        return;
      }
      const started = Date.now();
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve({ error: 'timeout', elapsedMs: Date.now() - started });
        }
      }, timeoutMs);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: new Date(pos.timestamp).toISOString(),
            ageMs: Date.now() - pos.timestamp,
          });
        },
        (err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve({
            error: `geo_err_${err.code}: ${err.message.slice(0, 100)}`,
            elapsedMs: Date.now() - started,
          });
        },
        { enableHighAccuracy: true, timeout: timeoutMs - 100, maximumAge: maxAgeMs },
      );
    });

  const lastResult = await fetchFix(30_000, 3000);
  const lastFix: GeoFix | null = 'error' in lastResult ? null : lastResult;

  // Fresh fix is the headline value for diagnostics. 8s timeout — long
  // enough that iOS Safari has time to actually warm the GPS, short
  // enough that Submit doesn't feel hung.
  const freshResult = await fetchFix(0, 8000);
  const freshFix: GeoFix | null = 'error' in freshResult ? null : freshResult;
  const freshFixError = 'error' in freshResult ? freshResult.error : undefined;
  const freshFixElapsedMs = 'error' in freshResult ? freshResult.elapsedMs : undefined;

  return { permission, lastFix, freshFix, freshFixError, freshFixElapsedMs };
}

/**
 * Active bandwidth test (#1873). Fetches a known-size endpoint and
 * measures wall-clock time. Browser-reported `navigator.connection.downlink`
 * is included separately as a passive estimate.
 *
 * The endpoint is a tiny Django view that returns exactly 100 KB of
 * zero bytes — fast, cacheable=no, no auth, no DB hit. See
 * apps/diagnostics/views.py bandwidth_test endpoint.
 */
async function runBandwidthTest(baseUrl: string): Promise<BandwidthTestResult> {
  const url = `${baseUrl}/api/diagnostics/bandwidth-test/?_=${Date.now()}`;
  const expectedBytes = 100 * 1024;
  const started = performance.now();
  try {
    const resp = await fetch(url, { method: 'GET', cache: 'no-store' });
    if (!resp.ok) {
      return {
        bytes: 0,
        elapsedMs: 0,
        mbps: 0,
        endpoint: url,
        error: `HTTP ${resp.status}`,
      };
    }
    const buf = await resp.arrayBuffer();
    const elapsedMs = performance.now() - started;
    const bytes = buf.byteLength;
    const mbps = bytes > 0 && elapsedMs > 0 ? (bytes * 8) / (elapsedMs / 1000) / 1_000_000 : 0;
    return {
      bytes,
      elapsedMs: Math.round(elapsedMs),
      mbps: Math.round(mbps * 100) / 100,
      endpoint: url,
      ...(bytes !== expectedBytes && { error: `expected ${expectedBytes} bytes, got ${bytes}` }),
    };
  } catch (err) {
    return {
      bytes: 0,
      elapsedMs: Math.round(performance.now() - started),
      mbps: 0,
      endpoint: url,
      error: err instanceof Error ? err.message.slice(0, 120) : String(err).slice(0, 120),
    };
  }
}

/**
 * Measure round-trip time to a dedicated empty-response endpoint
 * (/api/diagnostics/ping/). Cheap; no auth; no DB. Complements
 * bandwidth_test by isolating latency from throughput. Pre-#1873 this
 * pointed at /api/auth/status/ which doesn't exist and 404'd on every
 * report.
 */
async function runApiPing(baseUrl: string): Promise<ApiPingResult> {
  const url = `${baseUrl}/api/diagnostics/ping/?_=${Date.now()}`;
  const started = performance.now();
  try {
    const resp = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    return {
      url,
      ok: resp.ok,
      status: resp.status,
      elapsedMs: Math.round(performance.now() - started),
    };
  } catch {
    return {
      url,
      ok: false,
      status: 0,
      elapsedMs: Math.round(performance.now() - started),
    };
  }
}

async function getBatteryInfo(): Promise<BatteryInfo | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navAny = navigator as any;
  if (typeof navAny.getBattery !== 'function') return null;
  try {
    const battery = await navAny.getBattery();
    return {
      level: battery.level,
      charging: battery.charging,
      chargingTime: Number.isFinite(battery.chargingTime) ? battery.chargingTime : undefined,
      dischargingTime: Number.isFinite(battery.dischargingTime) ? battery.dischargingTime : undefined,
    };
  } catch {
    return null;
  }
}

function getAssociateInfo(): { id: number | null; email: string | null } {
  try {
    const raw = localStorage.getItem('associate');
    if (!raw) return { id: null, email: null };
    const a = JSON.parse(raw);
    return {
      id: a?.associateid ?? a?.AssociateID ?? null,
      email: a?.email ?? a?.Email ?? null,
    };
  } catch {
    return { id: null, email: null };
  }
}

/**
 * Impersonation telemetry (#1873 follow-up). When an admin uses More >
 * View as Other User, localStorage.impersonating holds the
 * impersonated associate; localStorage.associate is swapped to point
 * at them too (so API payloads carry the right associateid). We
 * surface both so admins can tell a report submitted "as Rob" was
 * actually filed by RMR, not Rob himself.
 */
function getImpersonationInfo(): {
  active: boolean;
  impersonated_associate_id: number | null;
  impersonated_name: string | null;
} {
  try {
    const raw = localStorage.getItem('impersonating');
    if (!raw) return { active: false, impersonated_associate_id: null, impersonated_name: null };
    const i = JSON.parse(raw);
    return {
      active: true,
      impersonated_associate_id: i?.associateid ?? null,
      impersonated_name: i?.name ?? null,
    };
  } catch {
    return { active: false, impersonated_associate_id: null, impersonated_name: null };
  }
}

export interface DiagnosticsReport {
  collectedAt: string;
  associate: { id: number | null; email: string | null };
  impersonation: ReturnType<typeof getImpersonationInfo>;
  app: {
    version: string;
    commit: string;
    branch: string;
    buildDate: string;
  };
  pwa: {
    displayMode: string;
    standalone: boolean;
    serviceWorker: SwInfo;
  };
  device: {
    userAgent: string;
    platform: string;
    language: string;
    languages: readonly string[];
    screenWidth: number;
    screenHeight: number;
    viewportWidth: number;
    viewportHeight: number;
    devicePixelRatio: number;
    maxTouchPoints: number;
    cookieEnabled: boolean;
    timeZone: string;
    /** navigator.deviceMemory (GB). Chrome+derivatives only. Not exposed on Safari. */
    deviceMemoryGb?: number;
    /** navigator.hardwareConcurrency — logical CPU count. */
    hardwareConcurrency?: number;
    /** Battery state, if exposed. iOS Safari does NOT expose this. */
    battery?: BatteryInfo | null;
    /** Whether the device reports any orientation API. */
    orientation?: string;
  };
  network: {
    online: boolean;
    connection: NetworkConnection | null;
    /** Active throughput test against /api/diagnostics/bandwidth-test/. */
    bandwidthTest: BandwidthTestResult;
    /** HEAD ping to /api/auth/status/ for latency. */
    apiPing: ApiPingResult;
  };
  geolocation: GeoState;
  storage: {
    estimate: { quota: number; usage: number; pct: number } | null;
    localStorage: Array<{ key: string; bytes: number }>;
    indexedDB: Array<{ db: string; store: string; count: number }>;
    /**
     * Opt-in full row dump of the offline IndexedDB stores, blobs elided
     * (#2207). Present only when the caller passes includeOfflineState — lets
     * support diagnose sync issues (orphaned queue rows, missing calllogids)
     * from the actual data instead of just counts.
     */
    offlineState?: OfflineStoreDump[];
  };
  recentErrors: ReturnType<typeof getRecentErrors>;
  /**
   * Chronological action log of last ~100 user actions (clicks,
   * navigation, form submits, API calls, errors). #1895 — lets support
   * see the exact path the rep took to the bug without needing full
   * DOM session replay.
   */
  actionLog: ReturnType<typeof getActionLog>;
  /**
   * Per-submission context (sub-page extras). Always present so the
   * server can discriminate by `context.type`. Defaults to 'quick' when
   * the caller doesn't pass anything.
   */
  context: DiagnosticsContext;
}

function getDisplayMode(): string {
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
  return 'browser';
}

/**
 * Resolve the API base URL the way the rest of the app does (mirrors
 * services/api.ts). Read here rather than imported to keep this module
 * stand-alone — used during error paths where api.ts might also be
 * the broken part we're debugging.
 */
function resolveApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
}

export interface CollectOptions {
  /**
   * Per-submission context block (#1873). Send Device Report omits
   * this and gets the default `{ type: 'quick' }`. GPS / Scanner pages
   * pass their own block.
   */
  context?: DiagnosticsContext;
  /**
   * Skip the bandwidth test + API ping (e.g. on a low-data plan, or
   * for unit tests). Defaults to false — every real submission runs them.
   */
  skipNetworkTests?: boolean;
  /**
   * Opt-in: include a full row dump of the offline IndexedDB stores (blobs
   * elided) for sync debugging (#2207). Off by default — it's the rep's own
   * visit data and adds payload size.
   */
  includeOfflineState?: boolean;
}

export async function collectDiagnostics(options: CollectOptions = {}): Promise<DiagnosticsReport> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection || null;
  const network: NetworkConnection | null = conn
    ? {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData,
        type: typeof conn.type === 'string' ? conn.type : undefined,
      }
    : null;

  const apiBase = resolveApiBaseUrl();
  const skipNet = options.skipNetworkTests === true;

  const [sw, storageEstimate, idb, geo, battery, bandwidthTest, apiPing] = await Promise.all([
    getServiceWorkerInfo(),
    getStorageEstimate(),
    getIndexedDbSummary(),
    getGeolocationState(),
    getBatteryInfo(),
    skipNet
      ? Promise.resolve<BandwidthTestResult>({
          bytes: 0,
          elapsedMs: 0,
          mbps: 0,
          endpoint: '(skipped)',
          error: 'skipped by caller',
        })
      : runBandwidthTest(apiBase),
    skipNet
      ? Promise.resolve<ApiPingResult>({
          url: '(skipped)',
          ok: false,
          status: 0,
          elapsedMs: 0,
        })
      : runApiPing(apiBase),
  ]);

  // #2207 — opt-in full offline-store dump (blobs elided). Run after the
  // parallel block so it never delays the always-on summary.
  const offlineState = options.includeOfflineState ? await getIndexedDbDump() : undefined;

  const displayMode = getDisplayMode();

  // navigator.deviceMemory exists on Chrome-family browsers; Safari
  // omits it. Stays undefined when not exposed rather than zero so the
  // payload can distinguish "unknown" from "low memory".
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deviceMemoryGb = (navigator as any).deviceMemory as number | undefined;

  let orientation: string | undefined;
  try {
    orientation = window.screen.orientation?.type;
  } catch {
    orientation = undefined;
  }

  return {
    collectedAt: new Date().toISOString(),
    associate: getAssociateInfo(),
    impersonation: getImpersonationInfo(),
    app: {
      version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
      commit: typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'unknown',
      branch: typeof __GIT_BRANCH__ !== 'undefined' ? __GIT_BRANCH__ : 'unknown',
      buildDate: typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : 'unknown',
    },
    pwa: {
      displayMode,
      standalone: displayMode === 'standalone',
      serviceWorker: sw,
    },
    device: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      languages: navigator.languages,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      maxTouchPoints: navigator.maxTouchPoints,
      cookieEnabled: navigator.cookieEnabled,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      deviceMemoryGb,
      hardwareConcurrency: navigator.hardwareConcurrency,
      battery,
      orientation,
    },
    network: {
      online: navigator.onLine,
      connection: network,
      bandwidthTest,
      apiPing,
    },
    geolocation: geo,
    storage: {
      estimate: storageEstimate,
      localStorage: getLocalStorageSummary(),
      indexedDB: idb,
      ...(offlineState ? { offlineState } : {}),
    },
    recentErrors: getRecentErrors(),
    actionLog: getActionLog(),
    context: options.context ?? { type: 'quick' },
  };
}

export function downloadDiagnosticsAsFile(report: DiagnosticsReport): void {
  const filename = `rdm-diagnostics-${report.associate.id ?? 'anon'}-${
    new Date().toISOString().replace(/[:.]/g, '-')
  }.json`;
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
